// Next polyfills are required to made IE11 work.
import "core-js/es";
import "core-js/features/url";
import "core-js/features/url-search-params";

import "assets/css/reset.css";
import "assets/css/fonts.css";
import "assets/css/core.css";

import React, {lazy, Suspense} from "react";
import ReactDOM from "react-dom";
import {Route, BrowserRouter as Router} from "react-router-dom";
import {Login} from "pages/Login";
import NotFound from "pages/NotFound";
import * as Sentry from "@sentry/react";
import {Integrations} from "@sentry/tracing";
import {PagePreloader} from "components/Preloader";
import {Provider} from "react-redux";
import {store} from "modules/store";
import {GigyaGate} from "components/GigyaGate";
import {NotAuthOnlyRoute, PrivateRoute, RecoverRoute} from "components/Route";
import {Translations} from "components/Translations";
import {GlobalPreloader} from "components/GlobalPreloader";
import {ComingSoonModal, CountriesRedirect, HowToPlayModal, VideoPlayerModal, OffSeasonModal} from "components";
import {GlobalErrors} from "components/GlobalErrors";

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || "";

type Factory<T> = () => Promise<{
	default: React.ComponentType<T>;
}>;

export function retryFailLoad<T>(fn: Factory<T>, retriesLeft = 5, interval = 1000): Factory<T> {
	return () =>
		new Promise((resolve, reject) => {
			fn()
				.then(resolve)
				.catch((error: unknown) => {
					setTimeout(() => {
						if (retriesLeft === 1) {
							reject(error);
							return;
						}

						retryFailLoad(fn, retriesLeft - 1, interval)().then(resolve, reject);
					}, interval);
				});
		});
}

const ProjectIndex = lazy(retryFailLoad(() => import(`apps/${process.env.REACT_APP_PROJECT_NAME || ""}`)));
const Backdoor = lazy(retryFailLoad(() => import("pages/Backdoor")));
const Brackets = lazy(retryFailLoad(() => import("pages/Brackets")));
const FanPredictions = lazy(retryFailLoad(() => import("pages/FanPredictions")));
const SharedBrackets = lazy(retryFailLoad(() => import("pages/SharedBrackets")));

const Leagues = lazy(retryFailLoad(() => import("pages/Leagues")));
const Ladder = lazy(retryFailLoad(() => import("pages/league/Ladder")));
const About = lazy(retryFailLoad(() => import("pages/league/About")));
const CreateLeague = lazy(retryFailLoad(() => import("pages/Leagues/CreateLeague")));
const JoinLeague = lazy(retryFailLoad(() => import("pages/Leagues/JoinLeague")));

const Prizes = lazy(retryFailLoad(() => import("pages/Prizes")));

const Help = lazy(retryFailLoad(() => import("./pages/Help")));
const GameGuidelines = lazy(retryFailLoad(() => import("./pages/Help/GameGuidelines")));
const ContactUs = lazy(retryFailLoad(() => import("./pages/Help/ContactUs")));
const FAQs = lazy(retryFailLoad(() => import("./pages/Help/FAQs")));
const GameRules = lazy(retryFailLoad(() => import("./pages/Help/GameRules")));
const PrizesPool = lazy(retryFailLoad(() => import("./pages/Help/PrizesPool")));
const Terms = lazy(retryFailLoad(() => import("./pages/Help/Terms")));
// const PrivacyPolicy = lazy(() => import("./pages/Help/PrivacyPolicy"));

const YourAccount = lazy(retryFailLoad(() => import("./pages/YourAccount")));
const Communications = lazy(retryFailLoad(() => import("./pages/yourAccount/Communications")));
const NFLAccount = lazy(retryFailLoad(() => import("./pages/yourAccount/NFLAccount")));
const DeactivateAccount = lazy(retryFailLoad(() => import("./pages/yourAccount/DeactivateAccount")));

const Register = lazy(retryFailLoad(() => import("./pages/Register")));
const Recover = lazy(retryFailLoad(() => import("./pages/Recover")));

Sentry.init({
	dsn: "https://6455030823e647d7876a21ee6f65e5ae@o151969.ingest.sentry.io/5521493",
	integrations: [
		new Integrations.BrowserTracing(),
		new Sentry.Integrations.Breadcrumbs({
			console: false,
		}),
	],
	environment: process.env.REACT_APP_SENTRY_ENV || "development",
	allowUrls: [
		".fanhubmedia.com",
		"www.superbowlchallenge.com.au",
		"superbowlchallenge.com.br",
		"superbowlchallenge.nfl.com",
		"nflchallenge.ran.de",
		"superbowlchallenge.co.uk",
	],
	denyUrls: [
		"quantcast",
		"xsca",
		// browser's extensions
		/extensions\//i,
		/^chrome:\/\//i,
		/^moz-extension:\/\//i,
	],
	sampleRate: 0.1,
});

ReactDOM.render(
	<Provider store={store}>
		<Router basename={"/"}>
			<GlobalPreloader />
			<ComingSoonModal />
			<OffSeasonModal />
			<GlobalErrors />
			<VideoPlayerModal />
			<HowToPlayModal />
			<Suspense fallback={<PagePreloader />}>
				<CountriesRedirect>
					<GigyaGate>
						<Translations>
							{PROJECT_NAME !== "" ? (
								<ProjectIndex>
									<NotAuthOnlyRoute path={"/login"} exact={true} component={Login} />
									<NotAuthOnlyRoute path={"/register"} exact={true} component={Register} />
									<RecoverRoute path={"/recover"} exact={true} component={Recover} />
									<NotAuthOnlyRoute path={"/backdoor"} exact={true} component={Backdoor} />
									<PrivateRoute path={"/"} exact={true} component={Brackets} />
									<PrivateRoute exact={true} path="/leagues" component={Leagues} />
									<PrivateRoute exact={true} path="/leagues/create" component={CreateLeague} />
									<PrivateRoute exact={true} path="/leagues/join" component={JoinLeague} />
									<PrivateRoute exact={true} path="/leagues/join/:code" component={JoinLeague} />
									<PrivateRoute exact={true} path="/league/:comp_id/ladder" component={Ladder} />
									<PrivateRoute exact={true} path="/league/:comp_id/about" component={About} />
									<PrivateRoute path="/account/update" component={YourAccount} />
									<PrivateRoute path="/account/nfl-account" component={NFLAccount} />
									<PrivateRoute path="/account/communications" component={Communications} />
									<PrivateRoute path="/account/deactivate-account" component={DeactivateAccount} />
									<Route path="/prizes" component={Prizes} />
									<Route exact={true} path="/help" component={Help} />
									<Route path="/help/contact-us" component={ContactUs} />
									<Route path="/help/faqs" component={FAQs} />
									<Route path="/help/game-guidelines" component={GameGuidelines} />
									<Route path="/help/rules" component={GameRules} />
									<Route path="/help/prizes" component={PrizesPool} />
									<Route path="/help/terms" component={Terms} />
									<Route path="/view/:userID" component={SharedBrackets} />
									<PrivateRoute path={"/fans"} exact={true} component={FanPredictions} />
									{/*<Route path="/help/privacy-policy" component={PrivacyPolicy} />*/}
									<Route component={NotFound} />
								</ProjectIndex>
							) : null}
						</Translations>
					</GigyaGate>
				</CountriesRedirect>
			</Suspense>
		</Router>
	</Provider>,
	document.getElementById("root")
);
